// The recording page every product shares (FEWeek.dc.html at laptop width, PhoneWeek.dc.html on a
// phone; design 5.3): the week's name and date, the Vimeo player that picks up where they left off
// and ticks at the product's watched share, the chapters with a copyable link to each minute, Ask
// Adam about this minute, the session summary, the 5 things, the key learnings with their minutes,
// the homework, the worksheet and the prompts, then the weeks either side. Its sections follow the
// product's published week layout (layout.js), in the same order on a phone, where the summary and
// Ask Adam come across too (sign-off.md, board 9).
//
//   <RecordingPage product={productRow} route={{ module, recording }} />   inside ProductMain
//
// A product's own week screen can draw this as it is, or take the parts it needs from here.
import { useEffect, useMemo, useRef, useState } from 'react'
import * as f from '../lib/format.js'
import { useMember } from '../app/member.jsx'
import { useNow } from '../lib/clock.js'
import { usePhone } from '../lib/media.js'
import { Link, useQueryParam } from '../lib/router.jsx'
import { copyText } from '../lib/clipboard.js'
import { useViewingAs, readOnlyControl, VIEWING_AS_LINE } from '../lib/viewAs.jsx'
import { SITE } from '../config/site.js'
import { chapterAt, chapterTime, linkStamp, parseStamp, rangeLine, splitStamps } from '../lib/timestamp.js'
import { useProductData } from './ProductData.jsx'
import { useLayout, LayoutSections } from './Layout.jsx'
import { defaultLayout, WEEK_PAGE } from './layout.js'
import { RecordingPlayer } from './Player.jsx'
import { ProductAnnouncement } from './ProductAnnouncement.jsx'
import { ResourceButton, VideoPanel } from './ResourceButton.jsx'
import { askAdam, loadRecordingContent, saveProgress, tickHomework } from './data.js'
import {
  homeworkCount, homeworkFor, learningBody, moduleName, moduleRecordings, neighbours, openItemIds, productAnnouncement, progressFor,
  progressPayload, recordedLineFor, weekEyebrow, isReleased,
} from './model.js'
import { sheetLabel, weekResources } from './release.js'
import { ChatIcon, Chevron, CopyIcon, KindIcon, LinkIcon, LockLine } from './Icons.jsx'
import '../styles/recording.css'

// the hub's 1 line for a page part that didn't load (stage 3's, approved)
export const LOAD_FAILED = `That didn't load. Check your connection and try again. If it keeps happening, email ${SITE.helpEmail}.`
const SAVE_FAILED = "That didn't save. Check your connection and try again."
const SEND_FAILED = "That didn't send. Check your connection and try again."

// The laptop grid FEWeek.dc.html draws: the 872px player and main column, the chapters and side
// column beside them
export const WEEK_COLUMNS = 'minmax(0, 872fr) minmax(300px, 408fr)'

const paragraphs = text => String(text || '').split(/\n\s*\n/).map(p => p.trim()).filter(Boolean)

// A label for where they are, for Ask Adam and the copied link: "Week 2", or the recording's title
export function whereLabel(module, recording) {
  return module?.label || recording?.title || ''
}

// The path of the page a minute link opens: /funding-engine/week-2
export function pagePath(product, module, recording) {
  const key = module?.key || recording?.key
  return `${product.path || ''}/${key || ''}`.replace(/\/$/, '')
}

// Their place, saved as they watch: the position each time, the furthest point and the seconds
// watched only ever growing, on top of what was saved before this visit. Never while staff view as a
// member. The database caps every number at the recording's length and sets watched_at itself.
function useProgressSaver(recording, duration) {
  const { me } = useMember()
  const viewingAs = useViewingAs()
  const { data, patch } = useProductData()
  const row = recording ? progressFor(data, recording.id) : null
  const base = useMemo(() => ({ furthest: row?.furthest_seconds || 0, watched: row?.seconds_watched || 0 }), [recording?.id])
  function onReport(report) {
    if (viewingAs || !recording) return
    const payload = progressPayload(base, report, duration)
    saveProgress(me.person.id, recording.id, payload)
      .then(saved => {
        if (!saved) return
        patch(d => ({ ...d, progress: [...d.progress.filter(p => p.recording_id !== recording.id), saved] }))
      })
      .catch(() => { /* the next report tries again */ })
  }
  return { row, onReport, canSave: !viewingAs }
}

export function RecordingPage({ product, route }) {
  const { data, patch } = useProductData()
  const { me, lib } = useMember()
  const phone = usePhone()
  const nowMs = useNow()
  const viewingAs = useViewingAs()
  const { module, recording } = route
  const released = recording ? isReleased(data, recording.id) : false
  const [content, setContent] = useState({ id: null, status: 'loading', row: null })

  useEffect(() => {
    if (!recording || !released) { setContent({ id: recording?.id || null, status: 'none', row: null }); return undefined }
    let live = true
    setContent({ id: recording.id, status: 'loading', row: null })
    loadRecordingContent(recording.id)
      .then(row => { if (live) setContent({ id: recording.id, status: row ? 'ready' : 'none', row }) })
      .catch(() => { if (live) setContent({ id: recording.id, status: 'error', row: null }) })
    return () => { live = false }
  }, [recording?.id, released])

  // the type's own week layout, or the live cohort's for a type that has none of its own
  const sections = useLayout(product, 'week', defaultLayout(product.type, 'week') || WEEK_PAGE)
  const row = content.id === recording?.id ? content.row : null
  const label = whereLabel(module, recording)
  const path = pagePath(product, module, recording)

  // a minute in the address (?t=): where Play starts, and the chapter it falls in
  const t = parseStamp(useQueryParam('t'))
  const [pos, setPos] = useState(null) // the player's own position, once it's playing
  const [seek, setSeek] = useState(null)
  const [played, setPlayed] = useState(false)
  const playerRef = useRef(null)
  const lastT = useRef(null)
  useEffect(() => {
    if (t == null || t === lastT.current) return
    lastT.current = t
    if (played) setSeek(s => ({ n: (s?.n || 0) + 1, seconds: t }))
    playerRef.current?.scrollIntoView({ block: 'center' })
  }, [t, played, row])

  const duration = row?.duration_seconds || data.content.find(c => c.recording_id === recording?.id)?.duration_seconds || 0
  const saver = useProgressSaver(recording, duration)
  const chapters = row?.chapters || []
  const here = played ? pos : t
  const chapter = here != null ? chapterAt(chapters, here) : null

  function jump(seconds) {
    setSeek(s => ({ n: (s?.n || 0) + 1, seconds }))
    setPos(seconds)
    setPlayed(true)
    playerRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }

  const watched = Boolean(saver.row?.watched_at)
  const savedSeconds = saver.row?.position_seconds || 0
  const furthest = saver.row?.furthest_seconds || 0
  // a recording with no chapters (a mastermind's group sessions) says nothing about chapters
  let posterLine = !chapters.length ? null : phone ? 'Tap a chapter to jump to it' : 'Press a chapter to jump straight to it'
  if (chapters.length && watched && phone) posterLine = 'Watched. Tap a chapter to jump to it'
  if (t != null) posterLine = `Press play to start at ${chapterTime(t)}`
  else if (!watched && savedSeconds > 10) posterLine = `Carry on from ${f.clock(savedSeconds)}`

  const homework = module ? homeworkFor(data, module.id) : []
  const files = weekResources(data, { moduleId: module?.id, recordingId: recording?.id }, nowMs)
  const announcement = productAnnouncement(lib.data, product.id, nowMs)
  const { prev, next } = neighbours(data, module)
  const keyMessages = row?.key_messages || []
  // each learning with its held sentences, put back only while their files are open to them
  const openItems = openItemIds(data)
  const learnings = (row?.key_learnings || []).map(l => ({ ...l, body: learningBody(l, openItems) }))

  const parts = {
    wnews: { place: 'full', when: Boolean(announcement), render: () => <ProductAnnouncement announcement={announcement} /> },
    video: {
      place: 'full',
      render: () => (
        <VideoSection product={product} module={module} recording={recording} row={row} label={label} path={path}
          chapters={chapters} chapter={chapter} here={here} phone={phone} playerRef={playerRef}
          player={(
            <RecordingPlayer title={`${product.name}, ${moduleName(module) || recording?.title || ''}`} content={row}
              savedSeconds={savedSeconds} startAt={t} seek={seek} canSave={saver.canSave} onReport={saver.onReport}
              watchedShare={Number(data.product?.watched_share) || 0.9} phone={phone}
              onPlay={() => setPlayed(true)} onTime={s => setPos(s)}
              poster={{ recorded: recordedLineFor(data.product || product, recording), line: posterLine, pct: duration ? (watched ? 100 : (furthest / duration) * 100) : 0, watched }} />
          )}
          onJump={jump} me={me} />
      ),
    },
    summary: { place: 'main', columns: WEEK_COLUMNS, when: Boolean(row?.summary), render: () => <Summary text={row.summary} product={data.product || product} /> },
    five: { place: 'main', columns: WEEK_COLUMNS, when: keyMessages.length > 0, render: () => <FiveThings items={keyMessages} module={module} phone={phone} /> },
    learn: { place: 'main', columns: WEEK_COLUMNS, when: learnings.length > 0, render: () => <KeyLearnings items={learnings} phone={phone} onJump={jump} /> },
    hw: {
      place: 'side', columns: WEEK_COLUMNS, when: homework.length > 0,
      render: () => <Homework items={homework} module={module} product={data.product || product} data={data} patch={patch} personId={me.person.id} viewingAs={viewingAs} />,
    },
    sheet: { place: 'side', columns: WEEK_COLUMNS, when: files.sheets.length > 0, render: () => <Worksheet cards={files.sheets} /> },
    prompts: { place: 'side', columns: WEEK_COLUMNS, when: files.prompts.length > 0, render: () => <Prompts cards={files.prompts} phone={phone} /> },
    // not a layout section: the weeks either side, at the foot of the main column (on a phone, the
    // next week and how to reach Adam, at the end)
    nav: { place: 'main', columns: WEEK_COLUMNS, when: Boolean(prev || next) || phone, render: () => <WeekNav product={product} data={data} prev={prev} next={next} phone={phone} /> },
  }

  const eyebrow = weekEyebrow(data, module, { phone })
  const title = module ? (module.title && !f.isPlaceholder(module.title) ? module.title : module.label) : recording?.title
  const siblings = module ? moduleRecordings(data, module.id) : []

  return (
    <div className="rp">
      <section className="rp-head">
        {eyebrow && <div className="rp-eyebrow">{eyebrow}</div>}
        <h1 className="rp-h1">{title}</h1>
        {siblings.length > 1 && <Parts product={product} siblings={siblings} current={recording} data={data} />}
      </section>
      {!recording || (!released && content.status !== 'loading') ? (
        <>
          <Locked product={product} module={module} data={data} />
          <WeekNav product={product} data={data} prev={prev} next={next} phone={phone} />
        </>
      ) : content.status === 'error' ? (
        <p className="rp-msg" role="alert">{LOAD_FAILED}</p>
      ) : content.status === 'loading' || !sections ? (
        <div className="rp-loading" aria-busy="true" />
      ) : (
        <LayoutSections sections={sections ? [...sections, { key: 'nav', shown: true, fixed: false }] : null} parts={parts} className="rp-layout" />
      )}
    </div>
  )
}

// The video, with the chapters beside it (under it on a phone), the toast for a copied link and
// Ask Adam about this minute
function VideoSection({ product, module, recording, label, path, chapters, chapter, here, phone, playerRef, player, onJump, me }) {
  const viewingAs = useViewingAs()
  const [toast, setToast] = useState(null)
  const [ask, setAsk] = useState({ open: false, text: '', sent: null, line: null, busy: false })

  async function copyLink(c) {
    const url = `${window.location.origin}${path}?t=${linkStamp(c.start_seconds)}`
    const shown = `${window.location.host}${path}?t=${linkStamp(c.start_seconds)}`
    const ok = await copyText(url)
    if (!ok) { setToast(`The link couldn't be copied. Here it is to copy yourself: ${shown}`); return }
    setToast(phone
      ? `Link copied. It opens ${label} at ${chapterTime(c.start_seconds)} for anyone logged in to the hub: ${shown}`
      : `Link copied: ${shown}. Anyone logged in to the hub opens it at that point.`)
  }

  const at = here == null ? 'the start' : chapterTime(here)
  const context = here == null
    ? `${label}, at the start.${chapters.length ? ' Pick a chapter to ask about a specific part.' : ''}`
    : `${label}, at ${chapterTime(here)}${chapter ? `, ${chapter.title}` : ''}`

  async function send() {
    const body = ask.text.trim()
    if (!body || ask.busy || viewingAs) return
    setAsk(a => ({ ...a, busy: true, line: null }))
    try {
      await askAdam({
        person_id: me.person.id, product_id: product.id, recording_id: recording?.id || null, module_id: module?.id || null,
        at_seconds: here == null ? 0 : here, chapter_title: chapter?.title || null,
        context_path: here == null ? path : `${path}?t=${linkStamp(here)}`, body,
      })
      setAsk({ open: false, text: '', sent: `Sent. Adam has your question with a link to ${label} at ${at}.`, line: null, busy: false })
    } catch {
      setAsk(a => ({ ...a, busy: false, line: SEND_FAILED }))
    }
  }

  return (
    <section className="rp-video" aria-label="The recording">
      <div className="rp-video-main" ref={playerRef}>
        {player}
        {phone && <span className="rp-sideways">Turn your phone sideways for full screen.</span>}
        {toast && <div className="rp-toast" role="status">{toast}</div>}
        <div className="rp-ask-row">
          <span className="rp-ask-context">{context}</span>
          <button type="button" className="rp-ask-btn" onClick={() => setAsk(a => ({ ...a, open: !a.open, sent: null }))}
            {...readOnlyControl(viewingAs)}>
            <ChatIcon />Ask Adam about this minute
          </button>
        </div>
        {viewingAs && <span className="rp-ask-note">{VIEWING_AS_LINE}</span>}
        {ask.open && !viewingAs && (
          <div className="rp-ask">
            <textarea aria-label="Your question" placeholder="What do you want to ask about this part?" value={ask.text}
              onChange={e => setAsk(a => ({ ...a, text: e.target.value }))} />
            <div className="rp-ask-foot">
              <span className="rp-ask-note">{ask.line || `Adam gets it with a link to ${label} at ${at}.`}</span>
              <button type="button" className="rp-ask-send" onClick={send} aria-busy={ask.busy || undefined}>Send to Adam</button>
            </div>
          </div>
        )}
        {ask.sent && <div className="rp-sent" role="status">{ask.sent}</div>}
      </div>
      {chapters.length > 0 && (
        <div className="rp-chapters">
          <div className="rp-chapters-head">Chapters</div>
          {chapters.map(c => (
            <div key={c.start_seconds} className={chapter && chapter.start_seconds === c.start_seconds ? 'rp-ch rp-ch--on' : 'rp-ch'}>
              <button type="button" className="rp-ch-seek" onClick={() => onJump(c.start_seconds)}>
                <span className="rp-ch-time">{chapterTime(c.start_seconds)}</span>
                <span className="rp-ch-title">{c.title}</span>
              </button>
              <button type="button" className="rp-ch-copy" aria-label={`Copy a link to ${chapterTime(c.start_seconds)}`} onClick={() => copyLink(c)}>
                <LinkIcon />
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

// A live course's recording is its session's; an on-demand course's is a module's recording
function Summary({ text, product }) {
  return (
    <section className="rp-card rp-summary">
      <h2 className="rp-h2">{product?.type === 'on_demand' ? 'Summary' : 'Session summary'}</h2>
      {paragraphs(text).map((p, i) => <p key={i} className="rp-summary-p">{p}</p>)}
    </section>
  )
}

function FiveThings({ items, module, phone }) {
  const n = items.length
  const head = phone || !module ? `If you only take ${n} things` : `If you only take ${n} things from ${module.label}`
  return (
    <section className="rp-five">
      <h2 className="rp-h2 rp-h2--gold">{n === 1 ? head.replace('things', 'thing') : head}</h2>
      {items.map((k, i) => (
        <div key={i} className="rp-five-row">
          <span className="rp-five-n">{i + 1}</span>
          <span className="rp-five-text">
            <span className="rp-five-head">{k.head}</span>
            {k.body && <span className="rp-five-body">{k.body}</span>}
          </span>
        </div>
      ))}
    </section>
  )
}

// A key learning's body: paragraphs, lines inside them, and each [mm:ss] a button that plays from there
function LearningBody({ body, onJump }) {
  return (
    <div className="rp-learn-body">
      {paragraphs(body).map((p, i) => {
        const lines = p.split('\n')
        const list = lines.length > 1 && lines.slice(1).every(l => /^(- |\d+\. )/.test(l.trim()))
        const render = l => splitStamps(l).map((bit, j) => (bit.stamp
          ? <button key={j} type="button" className="rp-stamp" onClick={() => onJump(bit.seconds)} aria-label={`Play from ${bit.stamp}`}>[{bit.stamp}]</button>
          : <span key={j}>{bit.text}</span>))
        if (list) {
          const [first, ...rest] = lines
          return (
            <div key={i} className="rp-learn-p">
              {!/^(- |\d+\. )/.test(first.trim()) && <p>{render(first)}</p>}
              <ul>{(/^(- |\d+\. )/.test(first.trim()) ? lines : rest).map((l, j) => <li key={j}>{render(l.trim().replace(/^- /, ''))}</li>)}</ul>
            </div>
          )
        }
        return <p key={i} className="rp-learn-p">{lines.map((l, j) => <span key={j}>{j > 0 && <br />}{render(l)}</span>)}</p>
      })}
    </div>
  )
}

function KeyLearnings({ items, phone, onJump }) {
  // FEWeek.dc.html opens the second on a laptop; PhoneWeek.dc.html opens none
  const [open, setOpen] = useState(phone ? null : Math.min(1, items.length - 1))
  return (
    <section className="rp-learn">
      <div className="rp-learn-head">
        <h2 className="rp-h2">{phone ? 'Key learnings' : 'Key learnings and timestamps'}</h2>
        {!phone && <span className="rp-learn-sub">Timestamps match the recording, so you can jump straight to any section.</span>}
      </div>
      {items.map((l, i) => (
        <div key={i} className="rp-learn-row">
          <button type="button" className="rp-learn-toggle" aria-expanded={open === i} onClick={() => setOpen(open === i ? null : i)}>
            <span className="rp-learn-titles">
              <span className="rp-learn-title">{l.title}</span>
              {phone && <span className="rp-learn-range">{rangeLine(l.start_seconds, l.end_seconds)}</span>}
            </span>
            {phone ? <Chevron open={open === i} /> : <span className="rp-learn-range">{rangeLine(l.start_seconds, l.end_seconds)}</span>}
          </button>
          {open === i && <LearningBody body={l.body} onJump={onJump} />}
        </div>
      ))}
    </section>
  )
}

// Homework: ticks saved as they go. "4 of 6 done" counts homework, never Week 6's commitments. The
// week's own note goes above the ticks; the product's note (FEWeek.dc.html's) under them, where the
// week has none of its own, and only for members who can WhatsApp Adam when it says to.
function Homework({ items, module, product, data, patch, personId, viewingAs }) {
  const [line, setLine] = useState(null)
  const count = homeworkCount(items, data.ticks)
  const own = paragraphs(module?.homework_note)
  const productNote = product.settings?.homework_note || ''
  const showProductNote = !own.length && productNote && (data.whatsappUrl || !/whatsapp/i.test(productNote))
  // Week 6's 7 are commitments, not homework to hand in
  const allCommitments = items.length > 0 && items.every(h => h.kind === 'commitment')
  const groups = []
  for (const h of items) {
    const g = groups[groups.length - 1]
    if (g && g.heading === (h.group_heading || null)) g.items.push(h)
    else groups.push({ heading: h.group_heading || null, items: [h] })
  }

  function toggle(h, done) {
    if (viewingAs) return
    setLine(null)
    patch(d => ({ ...d, ticks: done ? [...new Set([...d.ticks, h.id])] : d.ticks.filter(x => x !== h.id) }))
    tickHomework(personId, h.id, done).catch(e => {
      if (done && e?.code === '23505') return
      patch(d => ({ ...d, ticks: done ? d.ticks.filter(x => x !== h.id) : [...d.ticks, h.id] }))
      setLine(SAVE_FAILED)
    })
  }

  return (
    <section className="rp-card rp-card--line rp-hw">
      <div className="rp-hw-head">
        <h2 className="rp-h2 rp-h2--side">{allCommitments ? 'Your commitments' : 'Your homework'}</h2>
        {count && <span className="rp-hw-count">{count}</span>}
      </div>
      {own.map((p, i) => <p key={i} className="rp-hw-note rp-hw-note--own">{p}</p>)}
      {groups.map((g, gi) => (
        <div key={gi} className="rp-hw-group">
          {g.heading && <span className="rp-hw-heading">{g.heading}</span>}
          {g.items.map(h => {
            const done = data.ticks.includes(h.id)
            return (
              <label key={h.id} className={done ? 'rp-tick rp-tick--done' : 'rp-tick'}>
                <input type="checkbox" checked={done} onChange={e => toggle(h, e.target.checked)} {...readOnlyControl(viewingAs)} />
                <span className="rp-tick-text">
                  {h.body}
                  {h.time_label && <span className="rp-tick-time">{h.time_label}</span>}
                </span>
              </label>
            )
          })}
        </div>
      ))}
      {showProductNote && <p className="rp-hw-note">{productNote}</p>}
      {line && <p className="rp-msg" role="alert">{line}</p>}
    </section>
  )
}

function Worksheet({ cards }) {
  const [playing, setPlaying] = useState(null)
  return (
    <section className="rp-card rp-card--line rp-sheet">
      <h2 className="rp-h2 rp-h2--side">{cards.length > 1 ? 'Worksheets' : 'Worksheet'}</h2>
      {cards.map(c => (
        <div key={c.id} className="rp-sheet-wrap">
          <div className={c.open ? 'rp-sheet-row' : 'rp-sheet-row rp-sheet-row--locked'}>
            <span className="rp-sheet-label"><span className="rp-sheet-icon">{c.open ? <KindIcon kind={c.kind} size={20} /> : <LockLine size={20} />}</span>{sheetLabel(c)}</span>
            {c.open
              ? <ResourceButton card={c} className="rp-btn" onVideo={() => setPlaying(playing === c.id ? null : c.id)} />
              : <span className="rp-when">{c.when}</span>}
          </div>
          {playing === c.id && <VideoPanel card={c} onClose={() => setPlaying(null)} />}
        </div>
      ))}
    </section>
  )
}

function Prompts({ cards, phone }) {
  const [open, setOpen] = useState(null)
  return (
    <section className="rp-prompts">
      <h2 className="rp-h2 rp-h2--side">Prompts</h2>
      {cards.map(c => (
        <div key={c.id} className={phone ? 'rp-prompt rp-prompt--phone' : 'rp-prompt'}>
          <div className="rp-prompt-head">
            {phone
              ? <button type="button" className="rp-prompt-title rp-prompt-toggle" aria-expanded={open === c.id} onClick={() => setOpen(open === c.id ? null : c.id)}>{c.title}</button>
              : <span className="rp-prompt-title">{c.title}</span>}
            {c.open
              ? <ResourceButton card={c} className="rp-copy" icon={!phone ? <CopyIcon size={14} stroke={2} /> : null} short={phone} />
              : <span className="rp-when">{c.when}</span>}
          </div>
          {c.open && (!phone || open === c.id) && <div className="rp-prompt-text">{c.text}</div>}
        </div>
      ))}
    </section>
  )
}

// The weeks either side. On a phone, the next week alone, then how to reach Adam: WhatsApp while their
// intake runs (PhoneWeek.dc.html), the product's questions line otherwise (Adam, 3 Oct).
function WeekNav({ product, data, prev, next, phone }) {
  const link = m => (m && moduleOpen(data, m) ? `${product.path}/${m.key}` : null)
  const nextWord = next && /^week\b/i.test(next.label) ? 'Next week' : 'Next'
  const card = (m, word, cls, withTeaser) => {
    if (!m) return <span className={`${cls} rp-nav-card--empty`} aria-hidden="true" />
    const inner = (
      <>
        <span className="rp-nav-word">{word}</span>
        <span className="rp-nav-name">{moduleName(m)}</span>
        {withTeaser && m.teaser && <span className="rp-nav-teaser">{m.teaser}</span>}
      </>
    )
    const to = link(m)
    return to ? <Link to={to} className={cls}>{inner}</Link> : <span className={cls}>{inner}</span>
  }
  const email = data.product?.questions_email
  return (
    <section className="rp-nav" aria-label="The weeks either side">
      {phone ? (
        <>
          {next && card(next, nextWord, 'rp-nav-card', false)}
          {data.whatsappUrl
            ? <span className="rp-reach">If you're stuck, or want Adam's eyes on a post before it goes out, <a href={data.whatsappUrl} target="_blank" rel="noopener noreferrer">WhatsApp him</a>.</span>
            : email && <span className="rp-reach">If you're stuck, email <a href={`mailto:${email}`}>{email}</a>.</span>}
        </>
      ) : (
        <div className="rp-nav-row">
          {card(prev, 'Previous', 'rp-nav-card', false)}
          {card(next, nextWord, 'rp-nav-card rp-nav-card--next', true)}
        </div>
      )}
    </section>
  )
}

// A module opens when its first recording is released to them
function moduleOpen(data, m) {
  return moduleRecordings(data, m.id).some(r => isReleased(data, r.id))
}

// A module with several recordings (Property Cashflow For Life's Module 3): 1 link to each part
function Parts({ product, siblings, current, data }) {
  return (
    <nav className="rp-parts" aria-label="Parts">
      {siblings.map(r => {
        const name = r.part ? `Part ${r.part}: ${r.title}` : r.title
        const open = isReleased(data, r.id)
        if (r.id === current?.id) return <span key={r.id} className="rp-part rp-part--on" aria-current="page">{name}</span>
        return open && r.key
          ? <Link key={r.id} to={`${product.path}/${r.key}`} className="rp-part">{name}</Link>
          : <span key={r.id} className="rp-part rp-part--locked">{name}</span>
      })}
    </nav>
  )
}

// A week that hasn't opened for them yet: when, from their own intake's session where they have one
function Locked({ product, module, data }) {
  const s = module ? (data.sessions || []).filter(x => x.module_id === module.id && x.starts_at && x.status !== 'cancelled')
    .sort((a, b) => Date.parse(a.starts_at) - Date.parse(b.starts_at))[0] : null
  const what = module && /^week\b/i.test(module.label) ? 'This week'
    : module && /^module\b/i.test(module.label) ? 'This module' : 'This'
  // an on-demand course has no live sessions: a recording not up yet isn't open yet
  const line = product.type === 'on_demand' ? `${what} isn't open yet.`
    : s ? `${what} opens after its live session on ${f.longDate(s.starts_at)} at ${f.time(s.starts_at)}.`
    : `${what} opens after its live session.`
  return (
    <section className="rp-locked">
      <span className="rp-locked-icon"><LockLine size={28} /></span>
      <p className="rp-locked-line">{line}</p>
      {/* the masterclass's full name runs past a 360 phone on 1 line, so "the video", as Next up's "Open the video" */}
      <Link to={product.path || '/'} className="rp-btn rp-btn--line">Back to {product.type === 'masterclass' ? 'the video' : product.name}</Link>
    </section>
  )
}

